/**
 * @fileoverview Context for the active Account List ("My Lists") so any page
 * deep in the tree can read the user's active list and its resolved
 * department IDs without prop-drilling.
 * @module contexts/ListsContext
 *
 * Single source of truth: MainApp calls `useAccountLists()` once and passes
 * the result straight through this provider's `value` prop. We intentionally
 * do NOT call the hook inside the provider — doing so would create a second,
 * divergent copy of the selection state. TopNav keeps receiving props from
 * MainApp; data pages (Dashboard, Sankey, VCA) opt in via `useListsContext()`.
 *
 * Locked design decision: when a single department is selected it OVERRIDES
 * the list for data fetches, but the list context stays visible. Consumers
 * implement that by preferring an explicit department filter over
 * `selectedDepartmentIds` while still rendering the active-list affordance.
 */

import React, { createContext, useContext } from 'react';

/**
 * Shape mirrors the return value of `useAccountLists()`:
 *   { lists, listsLoading, listsError,
 *     selectedListId, selectedList, selectedListDetail, selectedListLoading,
 *     selectedMembers, selectedDepartmentIds,
 *     selectList, clearList }
 */
const ListsContext = createContext(null);

/**
 * Passthrough provider — `value` is the object returned by `useAccountLists()`.
 * @param {{ value: object, children: React.ReactNode }} props
 */
export function ListsProvider({ value, children }) {
  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

/**
 * Read the active account list state from anywhere under <ListsProvider>.
 * Returns a safe no-op shape when used outside a provider so pages that render
 * in isolation (tests, storybook) don't crash.
 * @returns {object} account lists state
 */
export function useListsContext() {
  const ctx = useContext(ListsContext);
  if (!ctx) {
    return {
      lists: [],
      listsLoading: false,
      listsError: null,
      selectedListId: null,
      selectedList: null,
      selectedListDetail: null,
      selectedListLoading: false,
      selectedMembers: [],
      selectedDepartmentIds: [],
      selectList: () => {},
      clearList: () => {},
    };
  }
  return ctx;
}

export default ListsContext;
